import { Alert, Button, Collapse, FormSection, Link } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import type { IServiceGroupSetting } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import type { IServiceOrganizationSettingFormSectionProps } from '../ServiceOrganizationSetting';
import { translateKey } from '../../../../../translate';
import { ServiceOrganizationSettingFormMode } from '../ServiceOrganizationSetting';
import { GroupHistoryTableEdit } from './GroupHistoryTableEdit';
import { GroupHistoryTableRead } from './GroupHistoryTableRead';

interface IGroupFormSectionProps extends IServiceOrganizationSettingFormSectionProps {
    currentServiceGroup: IServiceGroupSetting | null;
    serviceGroupSettingOptions: IServiceGroupSetting[];
    setCurrentServiceGroup: React.Dispatch<React.SetStateAction<IServiceGroupSetting | null>>;
}

export function GroupFormSection(props: IGroupFormSectionProps) {
    const {
        id,
        currentServiceGroup,
        serviceOrganizationGroupHistory,
        mode,
        serviceGroupSettingOptions,
        errors,
        validateOne,
        handleChange,
        setCurrentServiceGroup,
    } = props;
    const [showServiceOrganizationGroupHistory, setShowServiceOrganizationGroupHistory] = useState(false);
    const practitioner = useSelector((state: IReduxState) => state.authentication);
    const columns = ['Zorggroep', 'Aangepast door', 'Start- en einddatum'];

    useEffect(() => {
        if (mode === ServiceOrganizationSettingFormMode.edit) {
            setShowServiceOrganizationGroupHistory(true);
        } else {
            setShowServiceOrganizationGroupHistory(false);
        }
    }, [mode]);

    return (
        <>
            {(!!serviceOrganizationGroupHistory.length || serviceGroupSettingOptions.length > 0) && (
                <FormSection title={translateKey('common.careGroup')}>
                    <FormParagraphFormGroup label={translateKey('serviceOrganizationSetting.currentCareGroup')}>
                        {currentServiceGroup?.group.name || '-'}
                    </FormParagraphFormGroup>
                </FormSection>
            )}

            {errors.has('serviceGroupSettingId') && (
                <Alert appearance="warning">{getValidationErrorTranslation(errors.get('serviceGroupSettingId'))}</Alert>
            )}

            {mode === ServiceOrganizationSettingFormMode.edit && serviceGroupSettingOptions.length > 0 && (
                <Button
                    iconLeft="add"
                    onClick={async (_e) => {
                        onAddGroupHistory();
                    }}
                    style={{ margin: '16px 0' }}
                    variant="link"
                >
                    Zorggroep toevoegen
                </Button>
            )}

            {!!serviceOrganizationGroupHistory.length && (
                <>
                    <Collapse isOpen={showServiceOrganizationGroupHistory}>
                        {mode === ServiceOrganizationSettingFormMode.read ? (
                            <GroupHistoryTableRead
                                columns={columns}
                                serviceOrganizationGroupHistory={serviceOrganizationGroupHistory}
                            />
                        ) : (
                            <GroupHistoryTableEdit
                                columns={columns}
                                errors={errors}
                                handleChange={handleChange}
                                practitioner={practitioner}
                                serviceGroupSettingOptions={serviceGroupSettingOptions}
                                serviceOrganizationGroupHistory={serviceOrganizationGroupHistory}
                                setCurrentServiceGroup={setCurrentServiceGroup}
                                validateOne={validateOne}
                            />
                        )}
                    </Collapse>
                    <Link
                        color="minimal"
                        fontWeight="normal"
                        iconLeft={showServiceOrganizationGroupHistory ? 'caret-up' : 'caret-down'}
                        mt={6}
                        onClick={toggleShowServiceOrganizationGroupHistory}
                        size="sm"
                        variant="button"
                    >
                        {showServiceOrganizationGroupHistory
                            ? translateKey('serviceOrganizationSetting.hideCareGroupLogbook')
                            : translateKey('serviceOrganizationSetting.showCareGroupLogbook')}
                    </Link>
                </>
            )}
        </>
    );

    function toggleShowServiceOrganizationGroupHistory() {
        setShowServiceOrganizationGroupHistory(!showServiceOrganizationGroupHistory);
    }

    function onAddGroupHistory() {
        handleChange('serviceOrganizationGroupHistory', [
            {
                id: uuidv4(),
                serviceOrganizationSettingId: id,
                serviceGroupSettingId: null,
                lastUpdatedByPractitionerId: practitioner.id,
                lastUpdatedByPractitioner: practitioner,
                startDate: null,
                endDate: null,
                isDeleted: false,
                isNew: true,
                updated_at: new Date().toString(),
            },
            ...serviceOrganizationGroupHistory,
        ]);
    }
}
